import React from 'react';
import { CheckCircle2, Send, RotateCcw, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

/* LAMKO_EDIT_LINK_FEATURE */
export const submissionState = { editUrl: "", email: "" };

// Find the auto-saved form snapshot (flat key->value map) for the current page.
function _readSnapshot() {
  try {
    var path = window.location.pathname;
    var best = null;
    for (var i = 0; i < window.localStorage.length; i++) {
      var k = window.localStorage.key(i);
      if (!k || k.indexOf(path) === -1) continue;
      var v;
      try { v = JSON.parse(window.localStorage.getItem(k) || "null"); } catch (e) { continue; }
      if (v && typeof v === "object" && !Array.isArray(v)) {
        var obj = (v.data && typeof v.data === "object" && !Array.isArray(v.data)) ? v.data : v;
        if (Object.keys(obj).length) {
          if (k.indexOf("draft") !== -1) return obj; // strong match
          if (!best) best = obj;
        }
      }
    }
    return best || {};
  } catch (e) { return {}; }
}

function _findEmail(flat) {
  try {
    var vals = Object.keys(flat).map(function (k) { return flat[k]; })
      .filter(function (v) { return typeof v === "string"; });
    var re = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    var hit = vals.find(function (v) { return re.test(v.trim()); });
    return hit ? hit.trim() : "";
  } catch (e) { return ""; }
}

async function _persistSubmission(snapshot) {
  try {
    var email = _findEmail(snapshot);
    if (!email || !Object.keys(snapshot).length) return;
    var path = window.location.pathname;
    var slug = (path.split("/").filter(Boolean).pop() || "form").toLowerCase();
    var base = process.env.REACT_APP_BACKEND_URL;
    var r = await fetch(base + "/api/submission/save", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ slug: slug, name: "", email: email, data: snapshot, path: path }),
    });
    var j = await r.json();
    if (j && j.ok && j.id && j.token) {
      submissionState.editUrl = window.location.origin + path + "?draft=" + j.id + "&t=" + j.token;
      submissionState.email = email;
      try {
        await fetch(base + "/api/submission/resend-link", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ slug: slug, email: email }),
        });
      } catch (e2) {}
    }
  } catch (e) { /* non-fatal, never blocks submit */ }
}

export const EditLinkPanel = () => {
  var url = submissionState.editUrl;
  if (!url) return null;
  var copy = function (e) {
    try { navigator.clipboard.writeText(url); e.target.textContent = "Copied"; } catch (x) {}
  };
  return (
    <div className="mt-4 rounded-lg border border-orange-200 bg-orange-50 p-4 text-sm text-orange-900">
      <p className="font-semibold mb-1">Save your private edit link</p>
      <p className="mb-2 text-orange-800">
        Use this link to reopen and update this submission later without retyping.
        Keep it private, anyone with the link can edit your submission.
      </p>
      <div className="flex items-center gap-2 mb-2">
        <input readOnly value={url}
          onFocus={function (e) { e.target.select(); }}
          className="flex-1 rounded border border-orange-300 bg-white px-2 py-1 text-xs text-gray-700" />
        <button type="button" onClick={copy}
          className="rounded bg-orange-600 px-3 py-1 text-xs font-semibold text-white hover:bg-orange-700">
          Copy
        </button>
      </div>
      <p className="text-xs text-orange-700">
        We have also emailed this link to you. If you do not see it in your inbox, check your
        spam or junk folder. Gmail users, look in Promotions or Spam. Outlook users, look in
        Other or Junk. Please mark it as not spam so future messages reach you.
      </p>
    </div>
  );
};

export const ResendLinkBox = () => {
  var submit = async function (e) {
    e.preventDefault();
    var form = e.target;
    var email = (form.elements.email.value || "").trim();
    var note = form.querySelector(".resend-note");
    if (!email) return;
    var slug = (window.location.pathname.split("/").filter(Boolean).pop() || "form").toLowerCase();
    try {
      await fetch(process.env.REACT_APP_BACKEND_URL + "/api/submission/resend-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug: slug, email: email }),
      });
    } catch (x) {}
    if (note) note.textContent =
      "If a submission exists for that email, we have sent the edit link to it. Please check your inbox, and your spam or junk folder.";
  };
  return (
    <form onSubmit={submit} className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
      <p className="font-medium mb-1">Already submitted this form?</p>
      <div className="flex items-center gap-2">
        <input name="email" type="email" placeholder="Your email"
          className="flex-1 rounded border border-gray-300 bg-white px-2 py-1 text-xs" />
        <button type="submit"
          className="rounded bg-gray-700 px-3 py-1 text-xs font-semibold text-white hover:bg-gray-800">
          Email me my edit link
        </button>
      </div>
      <p className="resend-note text-xs text-gray-500 mt-1"></p>
    </form>
  );
};

export async function submitInquiry({ formType, sections, meta, files }) {
  const _lamkoSnapshot = _readSnapshot();
  const formData = new FormData();
  formData.append('form_type', formType);
  formData.append('payload', JSON.stringify({ sections, meta }));
  (files || []).forEach((f) => formData.append('files', f, f.name));

  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/services/inquire`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    let detail = 'Submission failed';
    try {
      const data = await response.json();
      if (data?.detail) detail = data.detail;
    } catch (e) { /* ignore */ }
    throw new Error(detail);
  }
  await _persistSubmission(_lamkoSnapshot);
  return response.json();
}

export const InquiryHero = ({ title, subtitle }) => (
  <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white pt-28 pb-14 md:pt-32 md:pb-20">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <Link to="/partner" className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-orange-400 mb-4 transition-colors" data-testid="back-to-partner">
        <ArrowLeft size={14} />
        Back to Request Services
      </Link>
      <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 leading-tight">{title}</h1>
      <p className="text-base md:text-lg text-gray-300 leading-relaxed">{subtitle}</p>
    </div>
  </section>
);

const _OrigSuccessBanner = () => (
  <div className="bg-orange-50 border-l-4 border-orange-600 rounded-md p-6 mb-8 flex items-start gap-4" data-testid="success-banner">
    <CheckCircle2 className="text-orange-600 flex-shrink-0 mt-0.5" size={28} />
    <div>
      <h3 className="text-lg font-bold text-orange-900 mb-1">Thank you! Your inquiry has been submitted successfully.</h3>
      <p className="text-sm text-orange-800 leading-relaxed">
        A confirmation copy has been sent to your email address. Our team will review your request and respond within 1-2 business days.
      </p>
    </div>
  </div>
);

export const PrivacyNote = () => (
  <p className="text-xs text-gray-500 leading-relaxed mt-6">
    By submitting this form, you agree to LAMKO's privacy policy. Your information will be used solely for processing this inquiry.
  </p>
);

export const FormActions = ({ onReset, isSubmitting }) => (
  <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4">
    <button
      type="button"
      onClick={onReset}
      disabled={isSubmitting}
      className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
      data-testid="reset-form-btn"
    >
      <RotateCcw size={14} />
      Reset form
    </button>
    <button
      type="submit"
      disabled={isSubmitting}
      className="inline-flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-orange-600 hover:bg-orange-700 rounded-md transition-colors shadow-sm disabled:opacity-60"
      data-testid="submit-inquiry-btn"
    >
      {isSubmitting ? 'Submitting…' : 'Submit inquiry'}
      {!isSubmitting && <Send size={14} />}
      {!isSubmitting && <span aria-hidden="true">→</span>}
    </button>
  </div>
);

export const SuccessBanner = () => (
  <>
    <_OrigSuccessBanner />
    <EditLinkPanel />
  </>
);
